import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { Item as CountryListItems } from "../../api/services/client/models";
import { useGetCountryListByPage } from "../../api/services/common/commonApis";
import {
  AddressParams,
  CreateNISParticipantRequestPayload,
  NISParticipantCreatedResponse,
  NisParticipantResponse,
  ProfessionalDetailsParams,
  ProfileParams,
  UpdateNISParticipantRequestPayload,
} from "../../api/services/nisParticipants/models";
import {
  useCreateNISParticipant,
  useUpdateNISParticipant,
} from "../../api/services/nisParticipants/nisParticipantsApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiFooterButtons,
  MuiTypography,
  OptionsDataType,
  WarningConfirmationModal,
} from "../../components";
import { ExpandCollapseButton } from "../../components/expandCollapseButton/ExpandCollapseButton";
import { InformationBoxWrapper } from "../../components/informationBox/InformationBox.style";
import { CountryField } from "../../components/textFieldWithValidation/TextFieldWithValidation";
import {
  BE_ERROR_CODES,
  FORM_ERROR_TIMEOUT_LONG,
  FORM_ERROR_TIMEOUT_SHORT,
  MODULE_PERMISSIONS,
  TIMEOUT_100,
} from "../../helpers/constants";
import { getDateFormatByCountry } from "../../helpers/constants/countryDateFormat";
import {
  PAGE_IDS,
  PAGE_NAME_FOR_COUNTRY_DROPDOWN,
  PAGE_OPERATION_TYPE,
  PAGE_URLS,
} from "../../helpers/constants/pageUrls";
import { commonSiteCoreKeys } from "../../helpers/constants/siteCoreKeys";
import {
  convertToUserFormat,
  convertToUTCFormat,
  getFieldUndefinedForPayloadParams,
  getParsedErrorMessage,
  getValueOrBlank,
  getVatPostalDetailsFromCountryKey,
  isErrorCode,
  isNotEmpty,
  replaceHashWithValue,
  setApiDataToDropDownOption,
} from "../../helpers/utils";
import useAccordion from "../../hooks/useAccordion";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { navigationDataSelector } from "../../redux/slices/appState/appStateSelector";
import {
  setIsDataModifiedCurrentPage,
  setNavigationDataClicked,
  toggleLoader,
} from "../../redux/slices/appState/appStateSlice";
import { CPParticipantActivityTimelineWrapper } from "../coachPortal/components/cpParticipantActivityTimeline/CPParticipantActivityTimeline.style";
import ParticipantActivityTimeline from "../createProgram/components/createProgramTabs/tabs/participant/components/participantActivityTimeline/ParticipantActivityTimeline";
import { activityTimelineSiteCoreKeys } from "../createProgram/siteCore/SiteCoreKeys";
import { CreateAddressDetails } from "./components/addressDetails/CreateAddressDetails";
import { NISStatus } from "./components/nisStatus/NISStatus";
import { CreateProfessionalDetails } from "./components/professionalDetails/CreateProfessionalDetails";
import { CreateProfile } from "./components/profile/CreateProfile";
import { CreateNISParticipantWrapper } from "./CreateNISParticipant.style";
import {
  CLIENT_SERVICES_EMAIL,
  CreateNISParticipantFormKey,
  NIS_PARTICIPANTS_ACCORDION_KEYS,
  VALUE_OTHER,
} from "./modelsAndSchemas/Models";
import { CreateNISParticipantSchema } from "./modelsAndSchemas/ValidationSchema";
import {
  createNISParticipantAddressDetailsSiteCoreKeys,
  createNISParticipantNISStatusSiteCoreKeys,
  createNISParticipantProfessionalDetailsSiteCoreKeys,
  createNISParticipantProfileSiteCoreKeys,
  nisParticipantHeaderSiteCoreKeys,
} from "./siteCore/SiteCoreKeys";
import { usePermissions } from "../../hooks/usePermissions";

interface CreateNISParticipantProps {
  isEditNISParticipant?: boolean;
  pageId?: string;
  nisParticipantId?: string;
  editNISParticipantData?: NisParticipantResponse;
}

const initialAccordionStatus = {
  [NIS_PARTICIPANTS_ACCORDION_KEYS.profile]: true,
  [NIS_PARTICIPANTS_ACCORDION_KEYS.professionalDetails]: false,
  [NIS_PARTICIPANTS_ACCORDION_KEYS.addressDetails]: false,
  [NIS_PARTICIPANTS_ACCORDION_KEYS.nisStatus]: false,
  [NIS_PARTICIPANTS_ACCORDION_KEYS.activityTimeline]: false,
};

const defaultValues = {
  // profile Section
  [CreateNISParticipantFormKey.nisId]: "",
  [CreateNISParticipantFormKey.participantFirstName]: "",
  [CreateNISParticipantFormKey.participantLastName]: "",
  [CreateNISParticipantFormKey.jobTitle]: "",
  [CreateNISParticipantFormKey.participantEmail]: "",
  [CreateNISParticipantFormKey.participantWorkEmail]: "",
  [CreateNISParticipantFormKey.participantMobile]: "",
  [CreateNISParticipantFormKey.participantMobileCountryCode]: "",
  [CreateNISParticipantFormKey.participantMobileISOCode]: "",

  // professional details section
  [CreateNISParticipantFormKey.country]: "",
  [CreateNISParticipantFormKey.clientEmployer]: "",
  [CreateNISParticipantFormKey.clientEmployerOther]: "",
  [CreateNISParticipantFormKey.projectManagerId]: "",
  [CreateNISParticipantFormKey.employeeId]: "",
  [CreateNISParticipantFormKey.releaseDate]: "",
  [CreateNISParticipantFormKey.hrContact]: "",
  [CreateNISParticipantFormKey.hrContactNameOther]: "",
  [CreateNISParticipantFormKey.hrContactEmailIdOther]: "",
  [CreateNISParticipantFormKey.severanceStatusSigned]: "",

  // address details section
  [CreateNISParticipantFormKey.address1]: "",
  [CreateNISParticipantFormKey.address2]: "",
  [CreateNISParticipantFormKey.address3]: "",
  [CreateNISParticipantFormKey.countryAddress]: "",
  [CreateNISParticipantFormKey.city]: "",
  [CreateNISParticipantFormKey.stateProvince]: "",
  [CreateNISParticipantFormKey.postalCode]: "",
};

const renderHeader = (label: string) => {
  return <AccordionHeaderText label={label} />;
};

const CreateNISParticipant: FC<CreateNISParticipantProps> = ({
  pageId = PAGE_IDS.CREATE_NIS_PARTICIPANT,
  isEditNISParticipant = false,
  nisParticipantId = "",
  editNISParticipantData,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const navigationData = useSelector(navigationDataSelector);
  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
  } = useAccordion(initialAccordionStatus);
  const {
    pageTitle,
    commonValidationMessages,
    commonLabels,
    fetchComponentTitle,
    fetchLabel,
    fetchRichTexts,
    fetchValidationMessage,
  } = useSiteCore();
  const { hasModulePermission } = usePermissions();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const countryVatPostalDataJson = fetchRichTexts(
    commonSiteCoreKeys.countryBusinessRuleListParent,
    commonSiteCoreKeys.countryBusinessRuleList,
  );

  const {
    profileTitle,
    jobTitleValidationMessage,
    nisParticipantCreatedSuccessMessage,
    nisParticipantDetailsAlreadyExistMessage,
    matchingNISParticipantEntryAlreadyExistMessage,
    updateNISParticipantConfirmationMessage,
    nisParticipantUpdatedMessage,
  } = createNISParticipantProfileSiteCoreKeys;

  const { activityTimeline } = activityTimelineSiteCoreKeys;

  const getProfileLabel = (key: string) => {
    return fetchLabel(profileTitle, key);
  };
  const getProfileRichText = (key: string) => {
    return fetchRichTexts(profileTitle, key);
  };

  const getComponentTitle = (key: string) => {
    return fetchComponentTitle(key);
  };

  const nisParticipantSuccessDataRef = useRef<NISParticipantCreatedResponse>();
  const [showExitConfirmationModal, setShowExitConfirmationModal] =
    useState(false);
  const [countryList, setCountryList] = useState<OptionsDataType[]>([]);
  const [showSuccessConfirmation, setShowSuccessConfirmation] = useState(false);
  const [showErrorWarningModal, setShowErrorWarningModal] = useState(false);
  const [showUpdateConfirmationModal, setShowUpdateConfirmationModal] =
    useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const warningErrorMessageRef = useRef<string>();
  const [countryVatPostalData, setCountryVatPostalData] = useState<
    CountryField[]
  >([]);
  const [isFormReset, setIsFormReset] = useState(false);

  const {
    mutateAsync: createNISParticipantMutation,
    isPending: isCreateNISParticipantPending,
    isError: isCreateNISParticipantError,
    error: createNISParticipantError,
    isSuccess: isCreateNISParticipantSuccess,
    data: createNISParticipantData,
  } = useCreateNISParticipant();

  const {
    mutateAsync: updateNISParticipantMutation,
    isPending: isUpdateNISParticipantPending,
    isError: isUpdateNISParticipantError,
    error: updateNISParticipantError,
    isSuccess: isUpdateNISParticipantSuccess,
  } = useUpdateNISParticipant();

  const { data: countryListData, isSuccess: isCountryListApiSuccess } =
    useGetCountryListByPage(
      PAGE_NAME_FOR_COUNTRY_DROPDOWN.PROGRAM,
      isEditNISParticipant ? PAGE_OPERATION_TYPE.EDIT : PAGE_OPERATION_TYPE.ADD,
    );

  useEffect(() => {
    if (countryVatPostalDataJson) {
      const countryVatPostalDataJsonParsed: CountryField[] = JSON.parse(
        countryVatPostalDataJson,
      );
      setCountryVatPostalData(countryVatPostalDataJsonParsed);
    }
  }, [countryVatPostalDataJson]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isCreateNISParticipant: {
          isVisible:
            isCreateNISParticipantPending || isUpdateNISParticipantPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ isCreateNISParticipant: { isVisible: false } }));
    };
  }, [isCreateNISParticipantPending, isUpdateNISParticipantPending]);

  const displayErrorWarningModal = (
    messageTemplate: string,
    dynamicValue: string,
  ) => {
    warningErrorMessageRef.current = replaceHashWithValue(
      messageTemplate,
      dynamicValue,
    );
    setShowErrorWarningModal(true);
  };

  useEffect(() => {
    const hasError = isCreateNISParticipantError || isUpdateNISParticipantError;
    const errorObj = createNISParticipantError ?? updateNISParticipantError;
    nisParticipantSuccessDataRef.current = undefined;
    if (hasError && isNotEmpty(errorObj)) {
      const isErrorCodeSame = isErrorCode(
        errorObj,
        BE_ERROR_CODES.NIS_PARTICIPANT_DUPLICATE_IN_ACTIVE_PROGRAM,
      );
      if (isErrorCodeSame) {
        const parsedErrorMessageProgramName = getParsedErrorMessage(errorObj);
        displayErrorWarningModal(
          getProfileRichText(nisParticipantDetailsAlreadyExistMessage),
          parsedErrorMessageProgramName,
        );
      } else displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
    }
  }, [
    isCreateNISParticipantError,
    createNISParticipantError,
    isUpdateNISParticipantError,
    updateNISParticipantError,
  ]);

  useEffect(() => {
    if (
      isCreateNISParticipantSuccess &&
      createNISParticipantData &&
      isNotEmpty(createNISParticipantData)
    ) {
      nisParticipantSuccessDataRef.current = createNISParticipantData;
      if (
        isNotEmpty(createNISParticipantData.warningCode) &&
        createNISParticipantData.warningCode ===
          BE_ERROR_CODES.NIS_PARTICIPANT_DUPLICATE_WITH_OPEN_STATUS
      ) {
        displayErrorWarningModal(
          getProfileRichText(matchingNISParticipantEntryAlreadyExistMessage),
          createNISParticipantData.nisId,
        );
        clearFormAndFields();
      } else {
        setShowSuccessConfirmation(true);
      }
    }
  }, [isCreateNISParticipantSuccess, createNISParticipantData]);

  useEffect(() => {
    if (isUpdateNISParticipantSuccess) {
      displayToast(getProfileLabel(nisParticipantUpdatedMessage));
      navigate(`${PAGE_URLS.VIEW_NIS_PARTICIPANT}/${nisParticipantId}`, {
        state: { from: location.state?.from },
      });
    }
  }, [isUpdateNISParticipantSuccess]);

  useEffect(() => {
    if (isCountryListApiSuccess && isNotEmpty(countryListData)) {
      const mappedData = setApiDataToDropDownOption(countryListData, true);
      if (isNotEmpty(mappedData)) {
        setCountryList(mappedData);
      }
    }
  }, [isCountryListApiSuccess, countryListData]);

  useEffect(() => {
    if (navigationData.onNavigationClicked) {
      setShowExitConfirmationModal(true);
    }
  }, [navigationData.onNavigationClicked]);

  const methods = useForm({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(
      CreateNISParticipantSchema(
        {
          ...commonValidationMessages,
          jobTitleValidationMessage: getProfileLabel(jobTitleValidationMessage),
        },
        getValueOrBlank(userCountryDateFormat),
      ),
    ),
  });

  const {
    formState: { isDirty, touchedFields, errors },
    reset,
    getValues,
    handleSubmit,
    setValue,
    setError,
    trigger,
  } = methods;

  useEffect(() => {
    if (isEditNISParticipant && editNISParticipantData) {
      const { profile, professionalDetails, address } = editNISParticipantData;

      const clientEmployerValue = isNotEmpty(professionalDetails?.employer)
        ? professionalDetails.employer?.id
        : VALUE_OTHER;
      const hrContactValue = isNotEmpty(professionalDetails?.hrContact)
        ? professionalDetails?.hrContact?.id
        : VALUE_OTHER;

      const clientServicesEmail = isNotEmpty(professionalDetails?.serviceId)
        ? professionalDetails?.serviceId
        : undefined;
      const projectManagerValue =
        professionalDetails?.projectManager?.id ?? clientServicesEmail ?? "";

      reset({
        // profile Section
        [CreateNISParticipantFormKey.nisId]: profile?.nisId,
        [CreateNISParticipantFormKey.participantFirstName]: profile?.firstName,
        [CreateNISParticipantFormKey.participantLastName]: profile?.lastName,
        [CreateNISParticipantFormKey.jobTitle]: profile?.jobTitle,
        [CreateNISParticipantFormKey.participantEmail]: profile?.email,
        [CreateNISParticipantFormKey.participantWorkEmail]: profile?.workEmail,
        [CreateNISParticipantFormKey.participantMobile]:
          profile?.mobileNumber?.number,
        [CreateNISParticipantFormKey.participantMobileCountryCode]:
          profile?.mobileNumber?.countryCode,
        [CreateNISParticipantFormKey.participantMobileISOCode]:
          profile?.mobileNumber?.isoCode,

        // professional details section
        [CreateNISParticipantFormKey.country]:
          professionalDetails?.country?.countryId,
        [CreateNISParticipantFormKey.clientEmployer]: clientEmployerValue,
        [CreateNISParticipantFormKey.clientEmployerOther]:
          professionalDetails?.otherEmployer,
        [CreateNISParticipantFormKey.projectManagerId]: projectManagerValue,
        [CreateNISParticipantFormKey.employeeId]:
          professionalDetails?.employeeId,
        [CreateNISParticipantFormKey.releaseDate]:
          professionalDetails?.releaseDate
            ? convertToUserFormat(
                professionalDetails?.releaseDate,
                userCountryDateFormat,
              )
            : undefined,
        [CreateNISParticipantFormKey.hrContact]: hrContactValue,
        [CreateNISParticipantFormKey.hrContactNameOther]:
          professionalDetails?.otherHRContactName,
        [CreateNISParticipantFormKey.hrContactEmailIdOther]:
          professionalDetails?.otherHRContactEmailId,
        [CreateNISParticipantFormKey.severanceStatusSigned]:
          professionalDetails?.severanceStatusSignedId,

        // address details section
        [CreateNISParticipantFormKey.address1]: address?.address1,
        [CreateNISParticipantFormKey.address2]: address?.address2,
        [CreateNISParticipantFormKey.address3]: address?.address3,
        [CreateNISParticipantFormKey.countryAddress]:
          address?.country?.countryId,
        [CreateNISParticipantFormKey.city]: address?.city,
      });

      if (isNotEmpty(address?.postalCode)) {
        setTimeout(() => {
          setValue(
            CreateNISParticipantFormKey.postalCode,
            getValueOrBlank(address?.postalCode),
          );
          setValue(
            CreateNISParticipantFormKey.stateProvince,
            getValueOrBlank(address?.stateId),
          );
        }, FORM_ERROR_TIMEOUT_LONG);
      }
    }
  }, [isEditNISParticipant, editNISParticipantData]);

  useEffect(() => {
    return () => {
      dispatch(setIsDataModifiedCurrentPage(false));
    };
  }, []);

  useEffect(() => {
    const isShowConfirmationPopup = isDirty;
    dispatch(setIsDataModifiedCurrentPage(isShowConfirmationPopup));
  }, [isDirty]);

  const handleExitConfirmationModalClose = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    if (showExitConfirmationModal) {
      setShowExitConfirmationModal(false);
    }
  };
  const handleExitConfirmationModalPrimaryButtonClick = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    dispatch(setIsDataModifiedCurrentPage(false));
    clearFormAndFields();
    setTimeout(() => {
      navigate(navigationData.navigationPath);
    }, TIMEOUT_100);
    setShowExitConfirmationModal(false);
  };

  const handleSecondaryCancelClick = () => {
    if (!isEditNISParticipant) {
      navigateToDashboard();
      return;
    }
    if (isNotEmpty(touchedFields)) {
      setShowCancel(true);
    } else navigate(-1);
  };

  const navigateToDashboard = () => {
    if (isNotEmpty(touchedFields)) {
      clearFormAndFields();
    }
    navigate(PAGE_URLS.DASHBOARD);
  };

  const clearFormAndFields = () => {
    reset();
    setIsFormReset(!isFormReset);
  };

  const checkPostalValidation = () => {
    const { countryAddress, postalCode } = getValues();
    let isValid = true;

    const addressCountryRules = getVatPostalDetailsFromCountryKey(
      getValueOrBlank(countryAddress),
      countryVatPostalData,
    );
    if (addressCountryRules?.postalCode.isMandatory && !postalCode) {
      setTimeout(() => {
        setError(CreateNISParticipantFormKey.postalCode, {
          type: "custom",
          message: fetchValidationMessage(
            commonSiteCoreKeys.requiredValidation,
          ),
        });
      }, FORM_ERROR_TIMEOUT_SHORT);

      isValid = false;
    } else if (errors.postalCode) {
      setTimeout(() => {
        setError(CreateNISParticipantFormKey.postalCode, errors.postalCode!);
      }, FORM_ERROR_TIMEOUT_SHORT);
      isValid = false;
    }

    return isValid;
  };

  const callNISParticipantApi = () => {
    const {
      // profile
      participantFirstName,
      participantLastName,
      participantEmail,
      participantWorkEmail,
      jobTitle,
      participantMobile,
      participantMobileCountryCode,
      participantMobileISOCode,

      // professional details
      country,
      clientEmployer,
      clientEmployerOther,
      projectManagerId,
      employeeId,
      releaseDate,
      hrContact,
      hrContactNameOther,
      hrContactEmailIdOther,
      severanceStatusSigned,

      // address details
      address1,
      address2,
      address3,
      countryAddress,
      city,
      stateProvince,
      postalCode,
    } = getValues();

    const profileParams: ProfileParams = {
      firstName: participantFirstName,
      lastName: participantLastName,
      email: participantEmail,
      ...getFieldUndefinedForPayloadParams({
        workEmail: participantWorkEmail,
        jobTitle: jobTitle,
        mobileNumber: {
          isoCode: participantMobileISOCode,
          countryCode: participantMobileCountryCode,
          number: participantMobile,
        },
      }),
    };

    const professionalDetailsParams: ProfessionalDetailsParams = {
      countryId: country,
      projectManagerId:
        projectManagerId === CLIENT_SERVICES_EMAIL
          ? undefined
          : projectManagerId,
      clientId: clientEmployer === VALUE_OTHER ? undefined : clientEmployer,
      ...getFieldUndefinedForPayloadParams({
        serviceId:
          projectManagerId === CLIENT_SERVICES_EMAIL
            ? CLIENT_SERVICES_EMAIL
            : undefined,
        otherEmployer: clientEmployerOther,
        employeeId: employeeId,
        releaseDate: convertToUTCFormat(releaseDate, userCountryDateFormat),
        hrContactId: hrContact === VALUE_OTHER ? undefined : hrContact,
        otherHRContactName: hrContactNameOther,
        otherHRContactEmailId: hrContactEmailIdOther,
        severanceStatusSignedId: severanceStatusSigned,
      }),
    };

    const addressParams: AddressParams = {
      ...getFieldUndefinedForPayloadParams({
        address1,
        address2,
        address3,
        city,
        stateId: stateProvince,
        postalCode,
        countryId: countryAddress,
      }),
    };

    const params: CreateNISParticipantRequestPayload = {
      profile: profileParams,
      professionalDetails: professionalDetailsParams,
      address: addressParams,
    };
    if (isEditNISParticipant) {
      const updateParams: UpdateNISParticipantRequestPayload = {
        ...params,
        nisParticipantId: nisParticipantId,
      };
      updateNISParticipantMutation(updateParams);
    } else {
      createNISParticipantMutation(params);
    }
  };

  const checkValidationAndCallApi = () => {
    trigger().then((isValid) => {
      if (!checkPostalValidation()) {
        return;
      }
      if (isValid) {
        handleSubmit(callNISParticipantApi)();
      }
    });
  };

  const handlePrimarySubmitClick = () => {
    if (isEditNISParticipant) {
      setShowUpdateConfirmationModal(true);
    } else {
      checkValidationAndCallApi();
    }
  };

  const closeModalHandler = () => setShowCancel(false);

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    navigate(-1);
  };

  const handleUpdateConfirmationModalClose = () => {
    setShowUpdateConfirmationModal(false);
  };

  const handleUpdateConfirmationModalPrimaryButtonClick = () => {
    setShowUpdateConfirmationModal(false);
    checkValidationAndCallApi();
  };

  const handleCloseConfirmationClick = () => {
    setShowSuccessConfirmation(false);
    clearFormAndFields();
  };

  const handleSuccessConfirmationPrimaryClick = () => {
    handleCloseConfirmationClick();
    if (
      nisParticipantSuccessDataRef.current &&
      isNotEmpty(nisParticipantSuccessDataRef.current)
    ) {
      const nisParticipantId = nisParticipantSuccessDataRef.current.id;
      navigate(`${PAGE_URLS.VIEW_NIS_PARTICIPANT}/${nisParticipantId}`);
    }
  };

  const handleWarningErrorCloseClick = () => {
    warningErrorMessageRef.current = undefined;
    setShowErrorWarningModal(false);
    nisParticipantSuccessDataRef.current &&
      handleSuccessConfirmationPrimaryClick();
  };

  return (
    <CreateNISParticipantWrapper data-testid={pageId}>
      <FormProvider {...methods}>
        <InformationBoxWrapper className="information-box">
          <MuiBox className="line" />
          <MuiTypography variant="h1Bold" className="text" component="h1">
            {isEditNISParticipant
              ? fetchLabel(
                  nisParticipantHeaderSiteCoreKeys.nisParticipantHeaderTitle,
                  nisParticipantHeaderSiteCoreKeys.editNISParticipantHeader,
                )
              : pageTitle}
          </MuiTypography>
        </InformationBoxWrapper>

        <ExpandCollapseButton
          accordionStatus={accordionStatus}
          handleExpandCollapseAllChange={handleExpandCollapseAllChange}
        />

        <AccordionHeader
          title={renderHeader(
            getComponentTitle(
              createNISParticipantProfileSiteCoreKeys.profileTitle,
            ),
          )}
          onChange={() => handleIndividualAccordionChange("profile")}
          isExpanded={accordionStatus.profile}
        >
          <CreateProfile
            isEditNISParticipant={isEditNISParticipant}
            editNISParticipantData={editNISParticipantData}
          />
        </AccordionHeader>

        <MuiBox className="separator-space" />

        <AccordionHeader
          title={renderHeader(
            getComponentTitle(
              createNISParticipantProfessionalDetailsSiteCoreKeys.professionalDetailsTitle,
            ),
          )}
          onChange={() =>
            handleIndividualAccordionChange("professionalDetails")
          }
          isExpanded={accordionStatus.professionalDetails}
        >
          <CreateProfessionalDetails
            isEdit={isEditNISParticipant}
            editNISParticipantData={editNISParticipantData}
            countryList={countryList ?? []}
            rawCountryListData={
              (countryListData as unknown as CountryListItems[]) ?? []
            }
          />
        </AccordionHeader>

        <MuiBox className="separator-space" />

        <AccordionHeader
          title={renderHeader(
            getComponentTitle(
              createNISParticipantAddressDetailsSiteCoreKeys.addressDetailsTitle,
            ),
          )}
          onChange={() => handleIndividualAccordionChange("addressDetails")}
          isExpanded={accordionStatus.addressDetails}
        >
          <CreateAddressDetails
            isEdit={isEditNISParticipant}
            countryList={countryList ?? []}
            countryVatPostalData={countryVatPostalData}
            editNISParticipantData={editNISParticipantData}
          />
        </AccordionHeader>

        <MuiBox className="separator-space" />

        <AccordionHeader
          title={renderHeader(
            getComponentTitle(
              createNISParticipantNISStatusSiteCoreKeys.nisStatusTitle,
            ),
          )}
          onChange={() => handleIndividualAccordionChange("nisStatus")}
          isExpanded={accordionStatus.nisStatus}
        >
          <NISStatus
            isEdit={isEditNISParticipant}
            editNISParticipantData={editNISParticipantData}
            nisParticipantId={nisParticipantId}
          />
        </AccordionHeader>

        <MuiBox className="separator-space" />

        {isEditNISParticipant && (
          <>
            <AccordionHeader
              title={renderHeader(getComponentTitle(activityTimeline))}
              onChange={() =>
                handleIndividualAccordionChange("activityTimeline")
              }
              isExpanded={accordionStatus.activityTimeline}
            >
              <CPParticipantActivityTimelineWrapper>
                <ParticipantActivityTimeline
                  pageId={pageId}
                  programId={nisParticipantId}
                  isFromNISParticipant={true}
                  nisParticipantStatus={
                    editNISParticipantData?.profile?.statusId
                  }
                  displayAddBtn={isEditNISParticipant}
                  showNextContactDate={false}
                  isFromCoachPortal={false}
                  hasDeletePermission={hasModulePermission(
                    MODULE_PERMISSIONS.NIS_DELETE,
                  )}
                />
              </CPParticipantActivityTimelineWrapper>
            </AccordionHeader>
            <MuiBox className="separator-space" />
          </>
        )}

        {showExitConfirmationModal && (
          <WarningConfirmationModal
            labelContent={commonLabels.leavingPageActionText}
            show={showExitConfirmationModal}
            onClose={handleExitConfirmationModalClose}
            onPrimaryButtonClick={handleExitConfirmationModalPrimaryButtonClick}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
      </FormProvider>

      <MuiFooterButtons
        secondaryButtonLabel={commonLabels.cancel}
        primaryButtonLabel={
          isEditNISParticipant ? commonLabels.update : commonLabels.submit
        }
        onPrimaryClick={handlePrimarySubmitClick}
        onSecondaryClick={handleSecondaryCancelClick}
      />

      {showSuccessConfirmation && (
        <WarningConfirmationModal
          show={showSuccessConfirmation}
          modalTitle={commonLabels.success}
          onClose={handleSuccessConfirmationPrimaryClick}
          onPrimaryButtonClick={handleSuccessConfirmationPrimaryClick}
          labelContent={replaceHashWithValue(
            getProfileLabel(nisParticipantCreatedSuccessMessage),
            getValueOrBlank(nisParticipantSuccessDataRef.current?.nisId),
          )}
          primaryButtonTitle={commonLabels.ok}
          showSecondaryButton={false}
          isLabelHTMLContent={true}
        />
      )}

      {showErrorWarningModal && warningErrorMessageRef?.current && (
        <WarningConfirmationModal
          show={showErrorWarningModal}
          modalTitle={commonLabels.warning}
          onClose={handleWarningErrorCloseClick}
          onPrimaryButtonClick={handleWarningErrorCloseClick}
          labelContent={warningErrorMessageRef?.current}
          primaryButtonTitle={commonLabels.ok}
          showSecondaryButton={false}
          isLabelHTMLContent={true}
        />
      )}

      {showUpdateConfirmationModal && (
        <WarningConfirmationModal
          show={showUpdateConfirmationModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleUpdateConfirmationModalClose}
          onPrimaryButtonClick={handleUpdateConfirmationModalPrimaryButtonClick}
          labelContent={getProfileLabel(
            updateNISParticipantConfirmationMessage,
          )}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
          isLabelHTMLContent={false}
        />
      )}
      {showCancel && (
        <WarningConfirmationModal
          show={showCancel}
          onClose={closeModalHandler}
          onPrimaryButtonClick={cancelConfirmHandler}
          secondaryButtonTitle={commonLabels.no}
          primaryButtonTitle={commonLabels.yes}
          labelContent={commonLabels.cancelActionText}
        />
      )}
    </CreateNISParticipantWrapper>
  );
};

export default CreateNISParticipant;
